import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { listarAplicaciones } from '../../services/aplicaciones';
import AppShell from '../../components/layout/AppShell';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/ui/EmptyState';

function formatFecha(fechaaplicacion) {
  if (!fechaaplicacion) return '—';
  try {
    return new Date(`${fechaaplicacion}T00:00:00`).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return fechaaplicacion;
  }
}

function hoyISO() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Métricas calculadas en el cliente a partir de listarAplicaciones(), que ya
// respeta la RLS existente (auth_user_id = auth.uid()). No se envía ni
// idproductor ni auth_user_id manualmente: la RLS sigue siendo la única
// barrera de lectura.
function calcularResumen(aplicaciones) {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const hoyIso = hoyISO();

  let countHoy = 0;
  let count7Dias = 0;
  let countMes = 0;
  let countActivas = 0;

  aplicaciones.forEach((a) => {
    if (a.estado === 'ACTIVA') countActivas += 1;

    if (!a.fechaaplicacion) return;
    if (a.fechaaplicacion === hoyIso) countHoy += 1;

    const fecha = new Date(`${a.fechaaplicacion}T00:00:00`);
    const diffDias = Math.round((hoy - fecha) / (1000 * 60 * 60 * 24));
    if (diffDias >= 0 && diffDias <= 6) count7Dias += 1;
    if (fecha.getFullYear() === hoy.getFullYear() && fecha.getMonth() === hoy.getMonth()) countMes += 1;
  });

  return { hoy: countHoy, dias7: count7Dias, mes: countMes, activas: countActivas };
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function InicioPage() {
  const { encargado, productor } = useAuth();
  const navigate = useNavigate();

  const [aplicaciones, setAplicaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    listarAplicaciones().then((result) => {
      if (!active) return;
      if (result.ok) {
        setAplicaciones(result.aplicaciones);
      } else {
        setError(result.message);
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  const resumen = useMemo(() => calcularResumen(aplicaciones), [aplicaciones]);
  const ultimas = useMemo(() => aplicaciones.slice(0, 5), [aplicaciones]);

  return (
    <AppShell>
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Hola, {encargado?.nombre}</h1>
          <p className="mt-1 text-sm text-gray-500">Productor: {productor?.nombre}</p>
        </div>

        <div>
          <p className="mb-3 text-sm font-medium text-gray-500">Resumen</p>
          {loading ? (
            <p className="text-sm text-gray-500">Cargando...</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
              <MetricCard label="Hoy" value={resumen.hoy} />
              <MetricCard label="Últimos 7 días" value={resumen.dias7} />
              <MetricCard label="Este mes" value={resumen.mes} />
              <MetricCard label="Activas" value={resumen.activas} />
            </div>
          )}
        </div>

        <div>
          <p className="mb-3 text-sm font-medium text-gray-500">Últimas aplicaciones</p>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-center text-sm text-red-700">{error}</p>}

          {!loading && !error && ultimas.length === 0 && (
            <EmptyState message="Todavía no hay aplicaciones registradas." />
          )}

          {!loading && ultimas.length > 0 && (
            <div className="flex flex-col gap-2">
              {ultimas.map((aplicacion) => (
                <button
                  key={aplicacion.id}
                  type="button"
                  onClick={() => navigate(`/aplicaciones/${aplicacion.id}`)}
                  className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-green-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-gray-500">{formatFecha(aplicacion.fechaaplicacion)}</p>
                    <p className="truncate font-medium text-gray-900">
                      {aplicacion.huerto || '—'} · {aplicacion.cuartel || '—'}
                    </p>
                  </div>
                  <StatusBadge estado={aplicacion.estado} />
                </button>
              ))}
            </div>
          )}

          <Link
            to="/aplicaciones"
            className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-green-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
          >
            Ver todas las aplicaciones →
          </Link>
        </div>
      </div>
    </AppShell>
  );
}

export default InicioPage;
